/**
 * Every string on the Review v0 screen (Review Flow v2.0, Step 1).
 *
 * Source of truth: YAR-550's COPY table — verbatim, sentence case, no
 * trailing period on a standalone toast, no exclamation marks. Change a
 * string there first, then here.
 *
 * The emails card strings are owned by `lib/emailsCardModel.ts` (B1) and
 * spread in, so the card and the status toast keep one vocabulary.
 */
import { EMAILS_CARD_COPY } from '@/lib/emailsCardModel';
import { ACTION_LABELS, countNoun } from '@/lib/emailStatusRun';

export const REVIEW_COPY = {
  title: 'Review',
  subtitle: 'What Yaruta found from your approved senders',

  tagChange: 'Change',
  tagCancelled: 'Cancelled',
  tagAddDate: 'Add date',

  /** Add date sheet (YAR-554): today's form labels, unchanged. */
  dateTimeTitle: 'Date & Time',
  dateTimeAllDay: 'All-day',
  dateTimeStart: 'Start',
  dateTimeEnd: 'End',
  dateTimeDone: 'Done',

  actionDontChange: "Don't change",
  actionKeepEvent: 'Keep event',
  actionDontAdd: "Don't add",
  /** A nested task's button name (E2: "one card can hold several"). */
  actionDontAddNamed: (title: string) => `Don't add ${title}`,

  toastNotChanged: 'Not changed',
  toastEventKept: 'Event kept',
  toastNotAdded: 'Not added',
  undo: 'Undo',

  /** '1 task' · '[n] tasks' (E-10: a change card no longer has a tasks row). */
  tasksRow: (n: number) => countNoun(n, 'task', 'tasks'),

  cancelNote: 'Confirming removes it from your schedule.',
  /** E-1: a cancellation whose event has open tasks. */
  cancelNoteTasks: (n: number) =>
    `Confirming removes it and cancels its ${n === 1 ? 'task' : 'tasks'}.`,

  confirmAll: (n: number) => `Confirm all ${n}`,
  /** Footer gate, unchanged from production. */
  needDate: (n: number) =>
    n === 1
      ? '1 event needs a date before you can confirm.'
      : `${n} events need a date before you can confirm.`,

  /** Screen-reader status while Confirm all runs; matches the button's count (E1). */
  confirming: (n: number) => `Confirming ${countNoun(n, 'item', 'items')}…`,

  toastAdded: 'Added to your schedule',
  toastRemoved: (n: number) => `${countNoun(n, 'item', 'items')} removed`,

  emptyTitle: "You're all caught up",
  emptyAction: 'See your schedule',

  /** YAR-550 COPY table (Proposed, Oct 22); YAR-555 AC8. */
  failHeadline: 'Something went wrong on our end',
  /** YAR-550 COPY table (Proposed, Oct 22); YAR-555 AC8. */
  failBody: "Nothing's lost. We're trying again automatically.",
  /** After the automatic retries (D3): Try again is Confirm all itself. */
  failBodyRetry: "Nothing's lost. Tap Confirm all to try again.",

  planLimitTitle: 'Limit reached, upgrade to continue',
  planLimitBody: (used: number, quota: number, plan: string) =>
    `You've confirmed ${used} of ${quota} emails on the ${plan} plan this month.`,
  planLimitAction: 'Upgrade plan',

  /** Details sheet Sources (YAR-551 D2): 'Sources · 1 email' · 'Sources · 2 emails'. */
  sourcesHeading: (n: number) => `Sources · ${countNoun(n, 'email', 'emails')}`,
  /** The Sources heading before the count is known (loading or failed). */
  sourcesTitle: 'Sources',
  /** Sources can't load (E1); `n` = the item's sourceEmailIds.length. */
  sourcesError: (n: number) =>
    `We couldn't load the ${n > 1 ? 'emails' : 'email'} just now. This happens sometimes.`,
  /** The emails card's retry label, shared. */
  sourcesRetry: ACTION_LABELS['try-again'],
  sourceEmail: 'EMAIL',
  sourceLatest: 'Latest',

  /** Details sheet (YAR-551 D2): title by type, the pencil, the row labels. */
  detailsEvent: 'Event Details',
  detailsTask: 'Task Details',
  detailsEdit: 'Edit',
  detailLocation: 'Location',
  detailChildren: 'Children',
  detailMembers: 'Members',
  detailSubtasks: 'Sub-tasks',

  firstSyncHint: 'What Yaruta finds will show here as each email is read.',
  emailDiscarded: 'Email discarded',

  ...EMAILS_CARD_COPY,
} as const;
